import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Building2, User, Archive } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { AdminShell } from './admin-shell';
import { cn } from '@/lib/utils';

interface OrgRow {
  orgId: string; name: string; kind: 'personal' | 'team';
  domain: string | null; createdBy: string; createdAt: string;
  archivedAt: string | null;
  memberCount: number; projectCount: number;
  creatorEmail: string | null;
}

export function AdminOrgsPage() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'orgs', includeArchived],
    queryFn: () => apiClient.adminListOrgs({ includeArchived }),
  });
  const orgs = (data?.data as OrgRow[] | undefined) ?? [];

  return (
    <AdminShell subtitle="All orgs across every tenant. Click an org to inspect or archive.">

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Orgs ({orgs.length})</h1>
          <p className="text-sm text-muted-foreground">Personal + team workspaces.</p>
        </div>
        <label className="text-xs text-muted-foreground inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            className="rounded border-border"
          />
          Include archived
        </label>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="divide-y divide-border">
          {isLoading && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">Loading…</div>
          )}
          {!isLoading && orgs.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">No orgs found.</div>
          )}
          {orgs.map((o) => (
            <Link
              key={o.orgId}
              to={`/admin/orgs/${o.orgId}`}
              className={cn(
                'px-5 py-3 flex items-center justify-between gap-4 hover:bg-muted/40 transition-colors',
                o.archivedAt && 'opacity-60',
              )}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-8 w-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
                  {o.kind === 'team'
                    ? <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    : <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate flex items-center gap-2 flex-wrap">
                    {o.name}
                    {o.archivedAt && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground inline-flex items-center gap-1">
                        <Archive className="h-3 w-3" /> archived
                      </span>
                    )}
                    {o.domain && (
                      <span className="text-[10px] text-muted-foreground">@{o.domain}</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {o.kind} · {o.memberCount} {o.memberCount === 1 ? 'member' : 'members'} · {o.projectCount} projects · creator {o.creatorEmail ?? '—'}
                  </p>
                </div>
              </div>
              <span className="text-xs text-muted-foreground shrink-0 font-mono">{o.orgId.slice(0, 10)}…</span>
            </Link>
          ))}
        </div>
      </Card>
    </AdminShell>
  );
}
